"use client"

import * as React from "react"
import { cn, countCharacters, truncateText } from "@/lib/utils"

export interface TextareaWithMaxLengthProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  containerClassName?: string;
  Extensions?: React.FunctionComponent;
  onMaxLengthExceeded?: (maxLength: number) => void;
  maxLengthExceededTip?: React.ReactNode;
  maxLengthExceededTipClassName?: string;
  showCounter?: boolean;
}

export const TextareaWithMaxLength = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, TextareaWithMaxLengthProps>(
  ({
    className,
    containerClassName = '',
    Extensions,
    rows,
    onMaxLengthExceeded,
    maxLengthExceededTip,
    maxLengthExceededTipClassName,
    showCounter = true,
    ...props
  }, ref) => {
    const { maxLength, onPaste, onChange, ...restProps } = props;
    const isSingleLine = rows === 1;
    const [showMaxLengthTip, setShowMaxLengthTip] = React.useState(false);
    const maxLengthTipTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    // 为 input 准备 props，排除 rows
    const inputProps = isSingleLine ? (() => {
      const { rows: _, ...inputRest } = restProps as typeof restProps & { rows?: number };
      return inputRest;
    })() : restProps;
    let charCount = 0;
    if (maxLength) {
      const { chinese = 0, english = 0 } = countCharacters(props.value?.toString() || '');
      charCount = chinese + english;
    }

    React.useEffect(() => {
      return () => {
        if (maxLengthTipTimerRef.current) {
          clearTimeout(maxLengthTipTimerRef.current);
        }
      };
    }, []);

    const getNextValueAfterPaste = (
      currentValue: string,
      selectionStart: number | null,
      selectionEnd: number | null,
      pastedText: string,
    ) => {
      const start = selectionStart ?? currentValue.length;
      const end = selectionEnd ?? currentValue.length;
      return `${currentValue.slice(0, start)}${pastedText}${currentValue.slice(end)}`;
    };

    const getTextLength = (value: string) => {
      const { chinese = 0, english = 0 } = countCharacters(value);
      return chinese + english;
    };

    const wouldExceedMaxLength = (value: string) => {
      if (!maxLength) return false;
      return getTextLength(value) > maxLength;
    };

    const triggerMaxLengthExceeded = (limit: number) => {
      onMaxLengthExceeded?.(limit);

      if (maxLengthTipTimerRef.current) {
        clearTimeout(maxLengthTipTimerRef.current);
      }
      setShowMaxLengthTip(true);
      maxLengthTipTimerRef.current = setTimeout(() => {
        setShowMaxLengthTip(false);
      }, 2200);
    };

    const handlePasteInput = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e as unknown as React.ClipboardEvent<HTMLTextAreaElement>);
      }
      if (e.defaultPrevented || !maxLength) return;

      const pastedText = e.clipboardData.getData("text");
      if (!pastedText) return;

      const target = e.currentTarget;
      const nextValue = getNextValueAfterPaste(
        target.value,
        target.selectionStart,
        target.selectionEnd,
        pastedText,
      );
      if (wouldExceedMaxLength(nextValue)) {
        e.preventDefault();
        target.value = truncateText(nextValue, maxLength);
        triggerMaxLengthExceeded(maxLength);
        onChange?.(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
      }
    };

    const handlePasteTextarea = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (onPaste) {
        onPaste(e);
      }
      if (e.defaultPrevented || !maxLength) return;

      const pastedText = e.clipboardData.getData("text");
      if (!pastedText) return;

      const target = e.currentTarget;
      const nextValue = getNextValueAfterPaste(
        target.value,
        target.selectionStart,
        target.selectionEnd,
        pastedText,
      );
      if (wouldExceedMaxLength(nextValue)) {
        e.preventDefault();
        target.value = truncateText(nextValue, maxLength);
        triggerMaxLengthExceeded(maxLength);
        onChange?.(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
      }
    };

    const normalizeInputValue = <T extends HTMLInputElement | HTMLTextAreaElement>(
      e: React.ChangeEvent<T>,
    ) => {
      if (!maxLength || !wouldExceedMaxLength(e.currentTarget.value)) {
        return;
      }

      e.currentTarget.value = truncateText(e.currentTarget.value, maxLength);
      triggerMaxLengthExceeded(maxLength);
    };

    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      normalizeInputValue(e);
      onChange?.(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
    };

    const handleChangeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      normalizeInputValue(e);
      onChange?.(e);
    };

    const handleKeyDownInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (props.onKeyDown) {
        props.onKeyDown(e as unknown as React.KeyboardEvent<HTMLTextAreaElement>);
      }
    };

    const handleKeyDownTextarea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    const handleInputInput = (e: React.FormEvent<HTMLInputElement>) => {
      if (maxLength) {
        const target = e.target as HTMLInputElement;
        const newCharCount = getTextLength(target.value || '');

        // 如果超过限制，截断到上限
        if (newCharCount > maxLength) {
          target.value = truncateText(target.value || '', maxLength);
          triggerMaxLengthExceeded(maxLength);
        }
      }

      // 调用原有的 onInput 处理器
      if (props.onInput) {
        props.onInput(e as unknown as React.FormEvent<HTMLTextAreaElement>);
      }
    };

    const handleInputTextarea = (e: React.FormEvent<HTMLTextAreaElement>) => {
      if (maxLength) {
        const target = e.target as HTMLTextAreaElement;
        const newCharCount = getTextLength(target.value || '');

        // 如果超过限制，截断到上限
        if (newCharCount > maxLength) {
          target.value = truncateText(target.value || '', maxLength);
          triggerMaxLengthExceeded(maxLength);
        }
      }

      // 调用原有的 onInput 处理器
      if (props.onInput) {
        props.onInput(e);
      }
    };

    const handleBeforeInputInput = (e: React.InputEvent<HTMLInputElement>) => {
      if (props.onBeforeInput) {
        props.onBeforeInput(e as unknown as React.InputEvent<HTMLTextAreaElement>);
      }
    };

    const handleBeforeInputTextarea = (e: React.InputEvent<HTMLTextAreaElement>) => {
      if (props.onBeforeInput) {
        props.onBeforeInput(e);
      }
    };

    return (
      <div className={
        cn(
          "relative w-full flex-1 rounded-lg bg-muted/50 h-fit flex flex-col",
          containerClassName,
        )
      }>
        {isSingleLine ? (
          <input
            type="text"
            {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)}
            className={cn(
              "flex h-full w-full rounded-lg px-3 py-2.5 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 bg-transparent placeholder-annotation-foreground",
              maxLength && "pr-12",
              className
            )}
            ref={ref as React.Ref<HTMLInputElement>}
            onKeyDown={handleKeyDownInput}
            onInput={handleInputInput}
            onBeforeInput={handleBeforeInputInput}
            onPaste={handlePasteInput}
            onChange={handleChangeInput}
          />
        ) : (
          <textarea
            {...restProps}
            className={cn(
              "flex h-full w-full rounded-lg p-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 resize-none bg-transparent placeholder-annotation-foreground",
              className
            )}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            rows={rows}
            onKeyDown={handleKeyDownTextarea}
            onInput={handleInputTextarea}
            onBeforeInput={handleBeforeInputTextarea}
            onPaste={handlePasteTextarea}
            onChange={handleChangeTextarea}
          ></textarea>
        )}
        {
          Extensions ? (<Extensions {...props}></Extensions>) : null
        }
        {
          maxLength && showMaxLengthTip ? (
            <div
              role="status"
              aria-live="polite"
              className={cn(
                "pointer-events-none absolute bottom-10 right-3 z-20 max-w-[calc(100%-1.5rem)] whitespace-nowrap rounded-2xl bg-background px-4 py-2 text-sm text-foreground shadow-[0_12px_32px_rgba(15,23,42,0.18)] ring-1 ring-border/60",
                maxLengthExceededTipClassName
              )}
            >
              {maxLengthExceededTip ?? `最多支持 ${maxLength} 字内容`}
            </div>
          ) : null
        }
        {
          (maxLength && showCounter) ? (
            <div
              className={
                cn(
                  "absolute bottom-3 right-3 text-annotation-foreground text-xs pointer-events-none",
                  charCount >= maxLength && "text-destructive"
                )
              }
            >
              {maxLength ? `${charCount} / ${maxLength}` : null}
            </div>
          ) : null
        }
      </div>
    )
  }
)

TextareaWithMaxLength.displayName = "TextareaWithMaxLength"
